import { Dtos, FileType, SuretyQuoteStatus, stripEmpty } from "@inrev/common";
import { useFetchBondFormTemplates } from "@portal/api/bond-request";
import { useAdminUpdateBondForm } from "@portal/api/bond-request/admin";
import type { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import {
	getAllowedBondFormUploadTypes,
	getFilteredBondFormTemplates,
} from "@portal/views/shared/bond/bondUtils";
import { useMemo } from "react";
import type { BondFormData } from "../../../types";
import { BondFormSection } from "./BondFormSection";
import { useBondForm } from "./useBondForm";

type AdminBondFormSectionProps = {
	request: AdminBondRequest;
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response;
};

const isDeleteAllowed = (status: AdminBondRequest["status"]) =>
	status !== SuretyQuoteStatus.approved &&
	status !== SuretyQuoteStatus.accepted &&
	status !== SuretyQuoteStatus.pending;

export const AdminBondFormSection = ({ request, bondForm }: AdminBondFormSectionProps) => {
	const { updateBondForm } = useAdminUpdateBondForm(request.id, request.suretyAccountId);
	const { bondFormTemplates: templates, bondFormTemplatesIsLoading } = useFetchBondFormTemplates();
	const contractSuretyType = stripEmpty(request.contractSuretyType);

	const filteredTemplates = useMemo(
		() => getFilteredBondFormTemplates(templates, contractSuretyType),
		[templates, contractSuretyType],
	);

	const typeLabels = useMemo(
		() => getAllowedBondFormUploadTypes(contractSuretyType),
		[contractSuretyType],
	);

	const savedBondForm = useMemo<BondFormData>(() => {
		if (bondForm.source === "upload") {
			return {
				templateId: "",
				upload: {
					type: bondForm.bondFormType ?? "",
					files: bondForm.uploadedFiles.map((file) => ({
						id: file.id,
						name: file.name,
						extension: file.extension,
						types: [FileType.blank_bond_form],
					})),
				},
			};
		}
		return { templateId: bondForm.templateId, upload: { type: "", files: [] } };
	}, [bondForm]);

	const { value, onChange } = useBondForm({ savedBondForm, submit: updateBondForm });

	return (
		<BondFormSection
			value={value}
			onChange={onChange}
			templates={filteredTemplates}
			typeLabels={typeLabels}
			allowDelete={isDeleteAllowed(request.status)}
			partial={value.templateId === "" && value.upload.files.length === 0}
			isLoading={bondFormTemplatesIsLoading}
		/>
	);
};
